/**
 * Applies the appearance to the document exactly like the prototype (design/reboot/themes.js `apply`,
 * ui.js `render`): `data-theme` = the theme's base, `data-accent`, `data-custom-theme`, the reading size,
 * the list density on <body>, and the theme's tokens as CSS custom properties on <html>. Only values that
 * passed `normalizeTheme` reach the DOM.
 *
 *   dark, light          stylesheet only (ui.css / themes) — accent variants keep working
 *   paper, midnight, mono colours (accent roles only when the accent is violet) + reading font
 *   custom               every token, as the prototype's custom theme (pane widths included)
 *
 * A theme the plan does not include renders as `dark` (engine.md §15 "Changing plan").
 */

import {
  PREDEFINED_THEME_IDS, type AppearanceSettings, type PlanLimits, type ReadingSettings, type ThemeDefinition, type ThemeId,
} from '@digester/core';
import { fontFamily, normalizeTheme } from './normalize.js';
import { STYLESHEET_THEMES, THEME_PRESETS } from './presets.js';

const ACCENT_ROLES: ReadonlySet<string> = new Set(['accent', 'accent-fill', 'accent-bg', 'selected']);

/** Which theme the plan lets this appearance use (locked → 'dark'). */
export function effectiveThemeId(theme: ThemeId, limits: Pick<PlanLimits, 'themes' | 'customTheme'>): ThemeId {
  if (theme === 'custom') return limits.customTheme ? 'custom' : 'dark';
  const index = PREDEFINED_THEME_IDS.indexOf(theme);
  return index >= 0 && index < limits.themes ? theme : 'dark';
}

export function themeLocked(theme: ThemeId, limits: Pick<PlanLimits, 'themes' | 'customTheme'>): boolean {
  return effectiveThemeId(theme, limits) !== theme;
}

/** The custom properties a theme writes, as [property, value] pairs (also used by tests). */
export function themeProperties(themeId: ThemeId, custom: ThemeDefinition, accent: AppearanceSettings['accent']): Array<[string, string]> {
  if (themeId !== 'custom') {
    if (STYLESHEET_THEMES.has(themeId)) return [];
    const preset = THEME_PRESETS[themeId];
    const out: Array<[string, string]> = [];
    for (const [key, value] of Object.entries(preset.colors)) {
      if (accent !== 'violet' && ACCENT_ROLES.has(key)) continue;
      out.push([`--${key}`, value]);
    }
    out.push(['--article-font', fontFamily(preset.readingFont, preset.readingFamily)]);
    out.push(['--article-line-height', String(preset.readingLineHeight)]);
    if (!preset.shadows) out.push(['--shadow', 'none']);
    return out;
  }
  const t = normalizeTheme(custom);
  const out: Array<[string, string]> = Object.entries(t.colors).map(([key, value]) => [`--${key}`, value]);
  out.push(['--ui-font', fontFamily(t.uiFont, t.uiFamily)]);
  out.push(['--article-font', fontFamily(t.readingFont, t.readingFamily)]);
  out.push(['--font-control', `${t.uiSize}px`]);
  out.push(['--font-label', `${Math.max(13, t.uiSize - 1)}px`]);
  out.push(['--font-small', `${Math.max(12, t.uiSize - 2)}px`]);
  out.push(['--reading-size', `${t.readingSize}px`]);
  out.push(['--heading-size', `${t.headingSize}px`]);
  out.push(['--ui-line-height', String(t.uiLineHeight)]);
  out.push(['--article-line-height', String(t.readingLineHeight)]);
  out.push(['--ui-letter-spacing', `${t.letterSpacing}px`]);
  const dims: Array<[string, keyof ThemeDefinition]> = [
    ['row-padding', 'rowPadding'], ['reader-padding', 'readerPadding'], ['section-gap', 'sectionGap'], ['control-height', 'controlHeight'],
    ['toolbar-height', 'toolbarHeight'], ['control-radius', 'radius'], ['line-width', 'borderWidth'], ['action-icon-size', 'iconSize'],
    ['custom-nav-width', 'navWidth'], ['custom-list-width', 'listWidth'], ['custom-details-width', 'detailsWidth'], ['article-width', 'articleWidth'],
  ];
  for (const [property, key] of dims) out.push([`--${property}`, `${String(t[key])}px`]);
  out.push(['--shadow', t.shadows ? '0 5px 16px #0002,0 1px 3px #0004' : 'none']);
  return out;
}

export interface AppearanceInput {
  appearance: AppearanceSettings;
  reading: ReadingSettings;
  customTheme: ThemeDefinition;
  limits: Pick<PlanLimits, 'themes' | 'customTheme'>;
}

let applied: string[] = [];

/** Write the appearance to the document. Returns the theme actually shown. */
export function applyAppearance(input: AppearanceInput, root: HTMLElement = document.documentElement, body: HTMLElement | null = document.body): ThemeId {
  const themeId = effectiveThemeId(input.appearance.theme, input.limits);
  for (const property of applied) root.style.removeProperty(property);
  applied = [];
  const custom = normalizeTheme(input.customTheme);
  const baseOf = themeId === 'custom' ? custom.base : THEME_PRESETS[themeId].base;
  root.dataset.theme = baseOf;
  root.dataset.themeId = themeId;
  root.dataset.accent = input.appearance.accent;
  root.dataset.customTheme = themeId === 'custom' ? 'true' : 'false';
  // Reading text size: the custom theme's own size wins, as in the prototype.
  root.style.setProperty('--reading-size', `${input.reading.fontSize}px`);
  applied.push('--reading-size');
  for (const [property, value] of themeProperties(themeId, custom, input.appearance.accent)) {
    root.style.setProperty(property, value);
    applied.push(property);
  }
  if (body) body.dataset.density = input.appearance.density;
  return themeId;
}
